/**
 * GameStateMachine - Manages game state transitions
 * Handles the flow: Spawning → Falling → Frozen → Releasing → Stabilizing
 */

/**
 * Possible game states
 */
export enum GameState {
  SPAWNING = 'SPAWNING',       // Brief spawn animation
  FALLING = 'FALLING',         // Piece falling with gravity
  FROZEN = 'FROZEN',           // Physics paused, player adjusting
  RELEASING = 'RELEASING',     // Brief release animation
  STABILIZING = 'STABILIZING', // Waiting for tower to settle
  GAME_OVER = 'GAME_OVER',     // Game ended
}

/**
 * Callback type for state change listeners
 */
type StateCallback = () => void;

/**
 * State machine for managing game phases
 */
export class GameStateMachine {
  private currentState: GameState = GameState.SPAWNING;
  private enterListeners: Map<GameState, StateCallback[]> = new Map();
  private exitListeners: Map<GameState, StateCallback[]> = new Map();

  /**
   * Get the current game state
   */
  getState(): GameState {
    return this.currentState;
  }

  /**
   * Check if currently in a specific state
   */
  isState(state: GameState): boolean {
    return this.currentState === state;
  }

  /**
   * Transition to a new state
   */
  transition(newState: GameState): void {
    if (this.currentState === newState) return;

    const previousState = this.currentState;

    // Call exit listeners for current state
    const exitCallbacks = this.exitListeners.get(previousState) || [];
    exitCallbacks.forEach(cb => cb());

    // Update state
    this.currentState = newState;

    // Call enter listeners for new state
    const enterCallbacks = this.enterListeners.get(newState) || [];
    enterCallbacks.forEach(cb => cb());
  }

  /**
   * Register a callback for when entering a state
   */
  onEnter(state: GameState, callback: StateCallback): void {
    if (!this.enterListeners.has(state)) {
      this.enterListeners.set(state, []);
    }
    this.enterListeners.get(state)!.push(callback);
  }

  /**
   * Register a callback for when exiting a state
   */
  onExit(state: GameState, callback: StateCallback): void {
    if (!this.exitListeners.has(state)) {
      this.exitListeners.set(state, []);
    }
    this.exitListeners.get(state)!.push(callback);
  }

  /**
   * Reset state machine to initial state
   */
  reset(): void {
    this.currentState = GameState.SPAWNING;
  }

  /**
   * Clear all listeners
   */
  clearListeners(): void {
    this.enterListeners.clear();
    this.exitListeners.clear();
  }
}
